
import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, TrendingUp, Users, DollarSign, Edit2, Trash2, Power, X, Save, AlertTriangle, MessageSquare, Send, Clock, CheckCircle2, User } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useBlockDeal, BlockDeal, BlockDealInput } from '@/shared/hooks/useBlockDeal';
import { seedBlockDeals } from '@/shared/utils/seedBlockDeals';
import { db } from '@/lib/firebase';
import { formatDisplayName } from '@/shared/utils/formatters';
import { collection, query, onSnapshot, orderBy, doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

interface Inquiry {
    id: string;
    dealId: string;
    dealTitle: string;
    userId: string;
    userEmail: string;
    message: string;
    status: 'pending' | 'replied';
    adminReply?: string;
    repliedAt?: Timestamp;
    createdAt?: Timestamp;
}

const emptyDeal: BlockDealInput = {
    title: '',
    asset: 'BTC',
    description: '',
    roi: '',
    period: '',
    progress: 0,
    status: 'Active',
    minInvestment: 1000000,
    targetAmount: 1000000000,
    currentAmount: 0,
    investorCount: 0,
    riskLevel: '보통',
    features: []
};

const AdminBlockDealPage: React.FC = () => {
    const { deals, loading, addDeal, updateDeal, deleteDeal } = useBlockDeal();
    const [activeTab, setActiveTab] = useState<'products' | 'inquiries'>('products');
    const [showModal, setShowModal] = useState(false);
    const [editingDeal, setEditingDeal] = useState<BlockDeal | null>(null);
    const [form, setForm] = useState<BlockDealInput>(emptyDeal);
    const [featureInput, setFeatureInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [seeded, setSeeded] = useState(false);

    // Inquiry states
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [inquiriesLoading, setInquiriesLoading] = useState(true);
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyText, setReplyText] = useState('');
    const [replySending, setReplySending] = useState(false);

    // Load inquiries
    useEffect(() => {
        const q = query(collection(db, 'block_deal_inquiries'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Inquiry[];
            setInquiries(items);
            setInquiriesLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Seed on first load if empty
    useEffect(() => {
        if (!loading && deals.length === 0 && !seeded) {
            seedBlockDeals().then((result) => {
                if (result) setSeeded(true);
            });
        }
    }, [loading, deals.length, seeded]);

    const openAddModal = () => {
        setEditingDeal(null);
        setForm(emptyDeal);
        setFeatureInput('');
        setShowModal(true);
    };

    const openEditModal = (deal: BlockDeal) => {
        setEditingDeal(deal);
        setForm({
            title: deal.title, asset: deal.asset, description: deal.description,
            roi: deal.roi, period: deal.period, progress: deal.progress, status: deal.status,
            minInvestment: deal.minInvestment, targetAmount: deal.targetAmount,
            currentAmount: deal.currentAmount, investorCount: deal.investorCount,
            riskLevel: deal.riskLevel, features: deal.features || []
        });
        setFeatureInput('');
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.title || !form.roi || !form.period) {
            alert('필수 항목을 입력해주세요 (상품명, 수익률, 기간)');
            return;
        }
        setSaving(true);
        try {
            if (editingDeal) {
                await updateDeal(editingDeal.id, form);
            } else {
                await addDeal(form);
            }
            setShowModal(false);
        } catch (e: any) {
            alert('저장 실패: ' + e.message);
        }
        setSaving(false);
    };

    const handleDelete = async (id: string) => {
        try {
            await deleteDeal(id);
            setDeleteConfirm(null);
        } catch (e: any) {
            alert('삭제 실패: ' + e.message);
        }
    };

    const toggleStatus = async (deal: BlockDeal) => {
        const newStatus = deal.status === 'Active' ? 'Inactive' : 'Active';
        await updateDeal(deal.id, { status: newStatus });
    };

    const addFeature = () => {
        if (featureInput.trim()) {
            setForm(prev => ({ ...prev, features: [...prev.features, featureInput.trim()] }));
            setFeatureInput('');
        }
    };

    const removeFeature = (index: number) => {
        setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
    };

    const handleReply = async (inquiryId: string) => {
        if (!replyText.trim()) {
            alert('답변 내용을 입력해주세요');
            return;
        }
        setReplySending(true);
        try {
            await updateDoc(doc(db, 'block_deal_inquiries', inquiryId), {
                adminReply: replyText,
                status: 'replied',
                repliedAt: serverTimestamp()
            });
            setReplyingTo(null);
            setReplyText('');
        } catch (e: any) {
            alert('답변 실패: ' + e.message);
        }
        setReplySending(false);
    };

    const activeDeals = deals.filter(d => d.status === 'Active').length;
    const totalInvested = deals.reduce((sum, d) => sum + (d.currentAmount || 0), 0);
    const totalInvestors = deals.reduce((sum, d) => sum + (d.investorCount || 0), 0);
    const pendingInquiries = inquiries.filter(i => i.status === 'pending').length;

    const formatDate = (ts?: Timestamp) => {
        if (!ts) return '-';
        const d = ts.toDate();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    if (loading) {
        return <div className="flex items-center justify-center py-20"><Briefcase className="h-12 w-12 text-indigo-500 animate-pulse" /></div>;
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h1 className="text-2xl font-black flex items-center gap-3">
                        <Briefcase className="h-7 w-7 text-indigo-500" /> 블록딜 상품 관리
                    </h1>
                    <p className="text-slate-500 font-bold text-sm uppercase tracking-widest">프리미엄 자산 관리</p>
                </div>
                {activeTab === 'products' && (
                    <button
                        onClick={openAddModal}
                        className="h-12 px-6 rounded-2xl bg-indigo-600 text-white font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-600/20"
                    >
                        <Plus className="h-4 w-4" /> 상품 추가
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 p-1 bg-slate-950 rounded-2xl border border-slate-800">
                <button
                    onClick={() => setActiveTab('products')}
                    className={cn(
                        "flex-1 h-12 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all",
                        activeTab === 'products' ? "bg-indigo-600 text-white shadow-lg" : "text-slate-400 hover:text-white"
                    )}
                >
                    <Briefcase className="h-4 w-4" />
                    상품 관리 ({deals.length})
                </button>
                <button
                    onClick={() => setActiveTab('inquiries')}
                    className={cn(
                        "flex-1 h-12 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all relative",
                        activeTab === 'inquiries' ? "bg-indigo-600 text-white shadow-lg" : "text-slate-400 hover:text-white"
                    )}
                >
                    <MessageSquare className="h-4 w-4" />
                    문의 관리 ({inquiries.length})
                    {pendingInquiries > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center animate-pulse">
                            {pendingInquiries}
                        </span>
                    )}
                </button>
            </div>

            {/* ============ PRODUCTS TAB ============ */}
            {activeTab === 'products' && (
                <>
                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { label: '활성 상품', value: `${activeDeals}개`, icon: Briefcase, color: 'text-blue-500', bg: 'bg-blue-500/10' },
                            { label: '총 투자액', value: `₩${(totalInvested / 100000000).toFixed(1)}억`, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                            { label: '총 투자자', value: `${totalInvestors}명`, icon: Users, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
                        ].map((stat, i) => (
                            <div key={i} className="p-6 rounded-[2rem] bg-slate-950 border border-slate-800 flex items-center gap-5">
                                <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center", stat.bg, stat.color)}>
                                    <stat.icon className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{stat.label}</p>
                                    <p className="text-xl font-black text-white">{stat.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Deals List */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {deals.map((deal) => (
                            <div key={deal.id} className="group p-6 rounded-[2.5rem] bg-slate-950 border border-slate-800 hover:border-indigo-500/30 transition-all duration-500 relative overflow-hidden shadow-xl">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-3xl rounded-full -mr-10 -mt-10"></div>
                                <div className="flex items-start justify-between relative z-10">
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 rounded-3xl bg-white/5 border border-white/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                                            <TrendingUp className="h-7 w-7 text-indigo-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-black text-white text-lg group-hover:text-indigo-400 transition-colors uppercase">{deal.title}</h3>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest italic">목표: ₩{(deal.targetAmount / 100000000).toFixed(0)}억</span>
                                                <span className={cn("text-[10px] font-black uppercase px-2 py-0.5 rounded-md", deal.status === 'Active' ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-800 text-slate-500")}>
                                                    {deal.progress}% 달성
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => toggleStatus(deal)} className={cn("w-10 h-10 rounded-xl border flex items-center justify-center transition-all", deal.status === 'Active' ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500" : "bg-slate-900 border-slate-800 text-slate-500")}>
                                            <Power className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => openEditModal(deal)} className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 hover:text-white transition-all">
                                            <Edit2 className="h-4 w-4" />
                                        </button>
                                        {deleteConfirm === deal.id ? (
                                            <button onClick={() => handleDelete(deal.id)} className="h-10 px-3 rounded-xl bg-red-600 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1 animate-pulse">
                                                <AlertTriangle className="h-3 w-3" /> 확인
                                            </button>
                                        ) : (
                                            <button onClick={() => setDeleteConfirm(deal.id)} className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-all">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-6 grid grid-cols-3 gap-3 relative z-10">
                                    <div className="p-3 rounded-xl bg-white/5 text-center">
                                        <p className="text-[9px] text-slate-500 font-black uppercase">수익률</p>
                                        <p className="text-sm font-black text-emerald-400">{deal.roi}</p>
                                    </div>
                                    <div className="p-3 rounded-xl bg-white/5 text-center">
                                        <p className="text-[9px] text-slate-500 font-black uppercase">기간</p>
                                        <p className="text-sm font-black text-white">{deal.period}</p>
                                    </div>
                                    <div className="p-3 rounded-xl bg-white/5 text-center">
                                        <p className="text-[9px] text-slate-500 font-black uppercase">투자자</p>
                                        <p className="text-sm font-black text-white">{deal.investorCount}명</p>
                                    </div>
                                </div>
                                <div className="mt-4 space-y-2 relative z-10">
                                    <div className="flex justify-between text-[10px] font-black text-slate-600 uppercase tracking-widest">
                                        <span>달성: ₩{(deal.currentAmount / 100000000).toFixed(1)}억</span>
                                        <span>리스크: {deal.riskLevel}</span>
                                    </div>
                                    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                                        <div className={cn("h-full transition-all duration-1000", deal.status === 'Active' ? "bg-indigo-600" : "bg-slate-700")} style={{ width: `${deal.progress}%` }}></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {deals.length === 0 && (
                            <div className="lg:col-span-2 text-center py-20 opacity-50">
                                <Briefcase className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                                <p className="text-xs font-black text-slate-500 uppercase tracking-widest">등록된 블록딜 상품이 없습니다</p>
                                <button onClick={openAddModal} className="mt-4 text-indigo-500 text-xs font-black uppercase hover:underline">+ 첫 상품 추가하기</button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ============ INQUIRIES TAB ============ */}
            {activeTab === 'inquiries' && (
                <div className="space-y-4">
                    {/* Inquiry Stats */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                <Clock className="h-5 w-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-slate-500 uppercase">대기 중</p>
                                <p className="text-2xl font-black text-amber-500">{pendingInquiries}건</p>
                            </div>
                        </div>
                        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-slate-500 uppercase">답변 완료</p>
                                <p className="text-2xl font-black text-emerald-500">{inquiries.length - pendingInquiries}건</p>
                            </div>
                        </div>
                    </div>

                    {/* Inquiry List */}
                    {inquiriesLoading ? (
                        <div className="text-center py-12"><MessageSquare className="h-8 w-8 text-slate-600 animate-pulse mx-auto" /></div>
                    ) : inquiries.length === 0 ? (
                        <div className="text-center py-20 opacity-50">
                            <MessageSquare className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-xs font-black text-slate-500 uppercase tracking-widest">접수된 문의가 없습니다</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {inquiries.map((inq) => (
                                <div key={inq.id} className="rounded-2xl bg-slate-950 border border-slate-800 overflow-hidden">
                                    {/* Inquiry Header */}
                                    <div className="p-5 flex items-start justify-between">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                                                <User className="h-5 w-5 text-indigo-400" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <span className="text-sm font-black text-white">{formatDisplayName({ id: inq.userId, userName: inq.userEmail })}</span>
                                                    <span className={cn(
                                                        "text-[9px] font-black px-2 py-0.5 rounded-md uppercase",
                                                        inq.status === 'pending' ? "bg-amber-500/10 text-amber-500" : "bg-emerald-500/10 text-emerald-500"
                                                    )}>
                                                        {inq.status === 'pending' ? '대기 중' : '답변 완료'}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-slate-500 font-bold mt-0.5">
                                                    상품: <span className="text-indigo-400">{inq.dealTitle}</span> · {formatDate(inq.createdAt)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* User Message */}
                                    <div className="px-5 pb-4">
                                        <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800/50">
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">문의 내용</p>
                                            <p className="text-sm text-slate-300 font-bold">{inq.message || '(메시지 없음)'}</p>
                                        </div>
                                    </div>

                                    {/* Admin Reply */}
                                    {inq.adminReply && (
                                        <div className="px-5 pb-4">
                                            <div className="p-4 rounded-xl bg-indigo-500/5 border border-indigo-500/20">
                                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">관리자 답변 · {formatDate(inq.repliedAt)}</p>
                                                <p className="text-sm text-slate-300 font-bold">{inq.adminReply}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Reply Input */}
                                    {replyingTo === inq.id ? (
                                        <div className="px-5 pb-5 space-y-3">
                                            <textarea
                                                value={replyText}
                                                onChange={e => setReplyText(e.target.value)}
                                                className="w-full h-24 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50 resize-none"
                                                placeholder="답변 내용을 입력하세요..."
                                                autoFocus
                                            />
                                            <div className="flex gap-2 justify-end">
                                                <button
                                                    onClick={() => { setReplyingTo(null); setReplyText(''); }}
                                                    className="h-10 px-4 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 text-xs font-black"
                                                >
                                                    취소
                                                </button>
                                                <button
                                                    onClick={() => handleReply(inq.id)}
                                                    disabled={replySending}
                                                    className="h-10 px-6 rounded-xl bg-indigo-600 text-white text-xs font-black flex items-center gap-2 hover:bg-indigo-500 disabled:opacity-50"
                                                >
                                                    <Send className="h-3 w-3" />
                                                    {replySending ? '전송 중...' : '답변 전송'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="px-5 pb-5">
                                            <button
                                                onClick={() => { setReplyingTo(inq.id); setReplyText(inq.adminReply || ''); }}
                                                className="h-10 px-5 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white text-xs font-black flex items-center gap-2 transition-all"
                                            >
                                                <MessageSquare className="h-3 w-3" />
                                                {inq.adminReply ? '답변 수정' : '답변하기'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ============ PRODUCT MODAL ============ */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 shadow-2xl">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-xl font-black text-white uppercase tracking-wider">
                                {editingDeal ? '상품 수정' : '신규 상품 등록'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 hover:text-white transition-all">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">상품명 *</label>
                                <input type="text" value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" placeholder="예: 비트코인(BTC) 기관형 상품" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">자산 유형</label>
                                    <select value={form.asset} onChange={e => setForm(prev => ({ ...prev, asset: e.target.value }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50">
                                        <option value="BTC">BTC</option><option value="ETH">ETH</option><option value="XRP">XRP</option><option value="USDT">USDT</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">상태</label>
                                    <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value as any }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50">
                                        <option value="Active">활성</option><option value="Inactive">비활성</option><option value="SoldOut">매진</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">설명</label>
                                <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} className="w-full h-24 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50 resize-none" placeholder="상품에 대한 상세 설명을 입력하세요" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">예상 수익률 *</label>
                                    <input type="text" value={form.roi} onChange={e => setForm(prev => ({ ...prev, roi: e.target.value }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" placeholder="예: 12.4%" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">계약 기간 *</label>
                                    <input type="text" value={form.period} onChange={e => setForm(prev => ({ ...prev, period: e.target.value }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" placeholder="예: 30일" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">목표 금액 (원)</label>
                                    <input type="number" value={form.targetAmount} onChange={e => setForm(prev => ({ ...prev, targetAmount: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">최소 투자금 (원)</label>
                                    <input type="number" value={form.minInvestment} onChange={e => setForm(prev => ({ ...prev, minInvestment: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">현재 모집액</label>
                                    <input type="number" value={form.currentAmount} onChange={e => setForm(prev => ({ ...prev, currentAmount: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">진행률 (%)</label>
                                    <input type="number" value={form.progress} onChange={e => setForm(prev => ({ ...prev, progress: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" min="0" max="100" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">투자자 수</label>
                                    <input type="number" value={form.investorCount} onChange={e => setForm(prev => ({ ...prev, investorCount: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">리스크 등급</label>
                                <select value={form.riskLevel} onChange={e => setForm(prev => ({ ...prev, riskLevel: e.target.value as any }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50">
                                    <option value="낮음">낮음</option><option value="보통">보통</option><option value="높음">높음</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">특장점</label>
                                <div className="flex gap-2 mb-2">
                                    <input type="text" value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeature())} className="flex-1 h-10 bg-slate-900 border border-slate-800 rounded-xl px-4 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50" placeholder="특장점 입력 후 Enter" />
                                    <button onClick={addFeature} className="h-10 px-4 bg-indigo-600 text-white rounded-xl text-xs font-black">추가</button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {form.features.map((f, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-indigo-500/10 text-indigo-400 text-[10px] font-black">
                                            {f}
                                            <button onClick={() => removeFeature(i)} className="text-indigo-300 hover:text-white"><X className="h-3 w-3" /></button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-4 mt-8">
                            <button onClick={() => setShowModal(false)} className="flex-1 h-14 rounded-2xl bg-slate-900 border border-slate-800 text-slate-400 font-black text-xs uppercase tracking-widest transition-all hover:bg-slate-800">
                                취소
                            </button>
                            <button onClick={handleSave} disabled={saving} className="flex-1 h-14 rounded-2xl bg-indigo-600 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-600/20 disabled:opacity-50">
                                <Save className="h-4 w-4" />
                                {saving ? '저장 중...' : (editingDeal ? '수정 완료' : '상품 등록')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBlockDealPage;
